import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, ClipboardList, FileText, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SEO from "@/components/app/SEO";

export default function CaseSubmittedPage() {
  const [sp] = useSearchParams();
  const caseId = sp.get("case") || "";
  const [caseData, setCaseData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!caseId) return;
    api.get(`/cases/${caseId}`).then(({ data }) => setCaseData(data)).catch(() => setCaseData(false));
  }, [caseId]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-16">
      <SEO title="Votre demande a bien été enregistrée" noindex />
      <div className="card-elevated p-8 space-y-6 text-center">
        <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto" aria-hidden="true" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">Votre demande a bien été enregistrée</h1>
          <p className="text-muted-foreground leading-relaxed">
            Votre dossier a été sauvegardé et transmis à notre équipe pour vérification. Un conseiller vérifiera les informations et les documents transmis avant toute validation définitive, tout paiement éventuel ou toute préparation d'envoi.
          </p>
          <p className="text-sm text-muted-foreground mt-3">
            Vous recevrez un email dès que votre dossier aura été vérifié. Si une information ou un document complémentaire est nécessaire, notre équipe vous contactera directement.
          </p>
        </div>

        {caseData && (
          <div className="rounded-lg border border-border p-5 text-left space-y-2 bg-secondary/40" data-testid="submitted-case-summary">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Numéro de dossier</span>
              <span className="font-mono font-semibold">{caseData.id?.slice(0, 8).toUpperCase()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Organisme</span>
              <span className="font-medium">{caseData.organization_name || "—"}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Date de soumission</span>
              <span>{new Date(caseData.updated_at || caseData.created_at).toLocaleString("fr-FR")}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Statut</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200 text-xs font-medium">
                <ClipboardList className="h-3 w-3" />Dossier en cours de vérification
              </span>
            </div>
          </div>
        )}

        <ul className="text-left text-sm text-muted-foreground space-y-2">
          <li className="flex gap-2"><Mail className="h-4 w-4 mt-0.5 shrink-0" />Un email de confirmation vous est envoyé (si votre boîte de réception le permet).</li>
          <li className="flex gap-2"><FileText className="h-4 w-4 mt-0.5 shrink-0" />Vous pouvez ajouter des documents complémentaires depuis votre espace tant que la revue n'a pas commencé.</li>
          <li className="flex gap-2"><ClipboardList className="h-4 w-4 mt-0.5 shrink-0" />Aucun paiement ni envoi postal n'est déclenché avant validation humaine.</li>
        </ul>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button className="flex-1 bg-gradient-brand text-white border-0 hover:opacity-90" onClick={() => nav("/espace/dossiers")} data-testid="submitted-cases-btn">
            Consulter mon dossier
          </Button>
          <Button variant="outline" className="flex-1" onClick={() => nav(`/resilier/${caseId}`)} data-testid="submitted-edit-btn">
            Modifier mes informations
          </Button>
        </div>
        <div className="pt-2">
          <Link to="/" data-testid="submitted-home-btn" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            ← Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
}
